import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useAdaptersApi } from '../apis/adapters-apis';
import { Adapter, AdapterOutput, AdapterParameters, AccessType } from '../types';
import { useToast } from "../components/ui/Toast";
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import AdapterForm, { AdapterFormRef } from './AdapterForm';
import { useAuth } from '../contexts/AuthContext';

const AddAdapterPage: React.FC = () => {
  console.log('AddAdapterPage rendering');
  const formRef = useRef<AdapterFormRef>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const adaptersApi = useAdaptersApi();
  const { user } = useAuth();
  const [isReadOnly] = useState(false);
  const [existingAdapterNames] = useState<string[]>([]);
  const [isSuccessfullySaved, setIsSuccessfullySaved] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // const baseUrl = `https://${process.env.REACT_APP_ENVIRONMENT}.agenticgenie.click/external-adapter/`;
  const baseUrl = `${process.env.REACT_APP_API_URL}/external-adapter/`;
  console.log('baseUrl', baseUrl);

  const initialAdapter = useMemo(() => ({
    adapterId: '',
    adapterName: '',
    adapterDescription: '',
    adapterUrlEndpoint: baseUrl,
    adapterTargetUrl: '',
    adapterAuthentication: '',
    adapterParameters: {
      parameters: [],
      testMode: true
    } as AdapterParameters,
    adapterOutput: {} as AdapterOutput,
    configuration: "AdapterConfig",
    isCore: false,
    ownerId: user?.sub || '',
    adapterTags: [],
    tags: [],
    accessControl: {
      viewAccessType: 'Owner Only' as AccessType,
      viewAllowedUsers: [],
      editAccessType: 'Owner Only' as AccessType,
      editAllowedUsers: [],
    },
  }), [user?.sub, baseUrl]);

  const [newAdapter, setNewAdapter] = useState<Adapter>(initialAdapter);

  const handleAdapterChange = useCallback((adapter: Adapter) => {
    setNewAdapter(prev => {
      const updatedAdapter = { ...adapter };
      
      // Ensure adapterUrlEndpoint is properly formatted
      if (updatedAdapter.adapterUrlEndpoint) {
        updatedAdapter.adapterUrlEndpoint = updatedAdapter.adapterUrlEndpoint.startsWith(baseUrl)
          ? updatedAdapter.adapterUrlEndpoint
          : baseUrl + updatedAdapter.adapterUrlEndpoint;
      } else {
        updatedAdapter.adapterUrlEndpoint = baseUrl;
      }

      return updatedAdapter;
    });
    setIsSuccessfullySaved(false);
  }, [baseUrl]);

  const hasUnsavedChanges = !isSuccessfullySaved && !isNavigating && Boolean(
    newAdapter.adapterName ||
    newAdapter.adapterDescription ||
    newAdapter.adapterUrlEndpoint !== baseUrl ||
    newAdapter.adapterAuthentication ||
    (newAdapter.adapterTags && newAdapter.adapterTags.length > 0) ||
    (newAdapter.adapterParameters && newAdapter.adapterParameters.parameters.length > 0)
  );

  // Configure the blocker to not block when isSuccessfullySaved or isNavigating is true
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked" && !isSuccessfullySaved) {
      setShowNavigationWarning(true);
      setPendingPath(blocker.location.pathname);
    }
  }, [blocker, isSuccessfullySaved]);

  const handleSubmit = async () => {
    if (!formRef.current?.validateForm()) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      const adapterToCreate = {
        ...newAdapter,
        adapterTargetUrl: newAdapter.adapterTargetUrl.trim(),
        adapterUrlEndpoint: newAdapter.adapterUrlEndpoint.startsWith(baseUrl)
          ? newAdapter.adapterUrlEndpoint.trim()
          : baseUrl + newAdapter.adapterUrlEndpoint.trim(),
        adapterName: newAdapter.adapterName.trim(),
        adapterDescription: newAdapter.adapterDescription.trim(),
      };

      const createdAdapter = await adaptersApi.createAdapter(adapterToCreate);
      console.log("Adapter created successfully:", createdAdapter);
      
      // Mark as successfully saved and prevent navigation warnings
      setIsSuccessfullySaved(true);
      setIsNavigating(true);
      setShowNavigationWarning(false);
      
      addToast({
        title: "Success",
        description: `Adapter ${newAdapter.adapterName} has been successfully created`,
        variant: "default",
        duration: 4000
      });
      
      // Use setTimeout to ensure state updates have been processed
      setTimeout(() => {
        const testPath = `/marketplace/admin/adapters/test/${createdAdapter.adapterId}`;
        console.log("Navigating to:", testPath);
        navigate(testPath, { replace: true });
      }, 0);
    } catch (error) {
      console.error('Error creating adapter:', error);
      addToast({
        title: "Error",
        description: `Failed to create adapter: ${String(error)}`,
        variant: "destructive",
        duration: 8000
      });
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingPath('/marketplace/admin/adapters');
    } else {
      setIsNavigating(true);
      navigate('/marketplace/admin/adapters');
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setIsNavigating(true);
    
    if (pendingPath) {
      navigate(pendingPath);
    } else if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingPath(null);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl mb-4">Add New Adapter</h1>
      <AdapterForm
        ref={formRef}
        adapter={newAdapter}
        onAdapterChange={handleAdapterChange}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={false}
        readOnly={isReadOnly}
        existingAdapterNames={existingAdapterNames}
        currentUserId={user?.sub || ''}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </div>
  );
};

export default AddAdapterPage;